/**
 * External dependencies
 */
import debugFactory from 'debug';
import page from 'page';
import url from 'url';
import { parse } from 'qs';
import React from 'react';
import ReactDom from 'react-dom';

/**
 * Internal dependencies
 */
import { setupRoutes } from 'sections-middleware';
import { setRoute as setRouteAction } from 'state/ui/actions';
import config from 'config';
import userFactory from 'lib/user';
import { setReduxStore } from 'lib/redux-bridge';
import { setCurrentUser, requestChargebeeCustomer } from 'state/current-user/actions';
import { createReduxStore } from 'state';
import initialReducer from 'state/reducer';
import * as controller from 'lib/controller';
import { requestCurrentSupportIfNotRequested } from 'state/current-support/actions';
import { requestCurrentRentalIfNotRequested } from 'state/current-rental/actions';
import { requestCurrentRetailerRentalIfNotRequested } from 'state/current-retailer-rental/actions';
import { requestCurrentCloudHostingIfNotRequested } from 'state/current-cloud-hosting/actions';
import { requestCurrentWebIfNotRequested } from 'state/current-web/actions';
import { requestCurrentDispatchIfNotRequested } from 'state/current-dispatch/actions';
import { requestCurrentCloudBackupIfNotRequested } from 'state/current-cloud-backup/actions';
import { requestCurrentBarcodeLookupIfNotRequested } from 'state/current-barcode-lookup/actions';
import { requestCurrentRoamIfNotRequested } from 'state/current-roam/actions';
import { requestCurrentTsPayIfNotRequested } from 'state/current-tspay/actions';
import { requestPlanNamesIfNotRequested } from 'state/plan-names/actions';
import { requestCurrentVisualDeckIfNotRequested } from 'state/current-visual-deck/actions';
import { requestCurrentUniversalImporterIfNotRequested } from 'state/current-universal-importer/actions';
import { render, hydrate } from 'lib/controller/utils';
import LoaderLayout from 'layout/loading';
import ErrorLayout from 'layout/error';
import { Provider } from 'react-redux';
import { getCurrentSupport } from 'state/current-support/selectors';
import { requestCouponsIfNotRequested } from 'state/coupons/actions';
import { requestAddonsIfNotRequested } from 'state/addons/actions';
import { requestPlansIfNotRequested as requestSupportPlansIfNotRequested } from 'state/support-plans/actions';
import { requestRetailerPlansIfNotRequested as requestRentalPlansIfNotRequested } from 'state/rental-plans/actions';
import { requestPlansIfNotRequested as requestCloudBackupPlansIfNotRequested } from 'state/cloud-backup-plans/actions';
import { requestPlansIfNotRequested as requestRoamPlansIfNotRequested } from 'state/roam-plans/actions';
import { requestPlansIfNotRequested as requestWebPlansIfNotRequested } from 'state/web-plans/actions';
import { requestPlansIfNotRequested as requestDispatchPlansIfNotRequested } from 'state/dispatch-plans/actions';
import { requestPlansIfNotRequested as requestVisualDeckPlansIfNotRequested } from 'state/visual-deck-plans/actions';
import { requestPlansIfNotRequested as requestUniversalImporterPlansIfNotRequested } from 'state/universal-importer-plans/actions';

debugFactory.enable('billing');
const debug = debugFactory('billing');

const setupContextMiddleware = (reduxStore) => {
    page('*', (context, next) => {
        // page.js url parsing is broken so we had to disable it with `decodeURLComponents: false`
        const parsed = url.parse(context.canonicalPath, true);
        context.prevPath = parsed.path === context.path ? false : parsed.path;
        context.query = parsed.query;
        context.hashstring = (parsed.hash && parsed.hash.substring(1)) || '';

        // set `context.hash` (we have to parse manually)
        if (context.hashstring) {
            try {
                context.hash = parse(context.hashstring);
            } catch (e) {
                debug('failed to query-string parse `location.hash`', e);
                context.hash = {};
            }
        } else {
            context.hash = {};
        }

        context.store = reduxStore;

        // client version of the isomorphic method for redirecting to another page
        context.redirect = (httpCode, newUrl = null) => {
            if (isNaN(httpCode) && !newUrl) {
                newUrl = httpCode;
            }

            return page.replace(newUrl, context.state, false, false);
        };

        next();
    });

    page.exit('*', (context, next) => {
        if (!context.store) {
            context.store = reduxStore;
        }
        next();
    });
};

const loadSectionsMiddleware = () => setupRoutes();

// if the user is not logged in
// then some routes should not be accessible
// hint: make all routes not acceessible and add prop to skip
const loggedOutMiddleware = (currentUser) => {
    // if (currentUser.get()) {
    //     return;
    // }

    if (currentUser.get() === null) {
       page.redirect('/login');
    }

    if (currentUser.get()) {
        return;
    } /*else {
        page.redirect('/logout');
    }*/

    page('/', () => {
        page.redirect('/dashboard');
    });
};

const loggedInMiddleware = async (currentUser, reduxStore) => {
    if (!currentUser.get()) {
        return;
    }

    // Primary: subscription data needed to render the dashboard — gate the loading screen on these
    const primaryItems = [
        ['Account details',    requestChargebeeCustomer()],
        ['Support plan',       requestCurrentSupportIfNotRequested()],
        ['Rental plan',        requestCurrentRentalIfNotRequested()],
        ['Cloud hosting',      requestCurrentCloudHostingIfNotRequested()],
        ['Web plan',           requestCurrentWebIfNotRequested()],
        ['Dispatch plan',      requestCurrentDispatchIfNotRequested()],
        ['Cloud backup',       requestCurrentCloudBackupIfNotRequested()],
        ['Roam plan',          requestCurrentRoamIfNotRequested()],
        ['TS Pay',             requestCurrentTsPayIfNotRequested()],
        ['Visual Deck',        requestCurrentVisualDeckIfNotRequested()],
        ['Universal Importer', requestCurrentUniversalImporterIfNotRequested()],
    ];

    // Secondary: plan catalogs + coupons/addons only needed on subscribe pages — fire early but don't block
    const secondaryItems = [
        ['Plan names',         requestPlanNamesIfNotRequested()],
        ['Coupons',            requestCouponsIfNotRequested()],
        ['Add-ons',            requestAddonsIfNotRequested()],
        ['Support plans',      requestSupportPlansIfNotRequested()],
        ['Rental plans',       requestRentalPlansIfNotRequested()],
        ['Cloud Backup plans', requestCloudBackupPlansIfNotRequested()],
        ['Roam plans',         requestRoamPlansIfNotRequested()],
        ['Web plans',          requestWebPlansIfNotRequested()],
        ['Dispatch plans',     requestDispatchPlansIfNotRequested()],
        ['Visual Deck plans',  requestVisualDeckPlansIfNotRequested()],
        ['Importer plans',     requestUniversalImporterPlansIfNotRequested()],
    ];

    reduxStore.dispatch({ type: 'LOADING_INIT', total: primaryItems.length + secondaryItems.length });

    const dispatch = ([label, action]) =>
        reduxStore.dispatch(action).finally(() =>
            reduxStore.dispatch({ type: 'LOADING_ITEM_COMPLETE', label })
        );

    // Both groups fire immediately — secondary runs in the background
    const primaryFetch = Promise.allSettled(primaryItems.map(dispatch));
    Promise.allSettled(secondaryItems.map(dispatch));

    page('/logout', () => {
        currentUser.clear();
        return (window.location = '/login');
    });

    page('*', async (context, next) => {
        try {
            context.layout = (
                <Provider store={context.store}>
                    <LoaderLayout />
                </Provider>
            );
            hydrate(context);
            await primaryFetch;
        } catch (error) {
            context.layout = (
                <Provider store={context.store}>
                    <ErrorLayout />
                </Provider>
            );
            hydrate(context);
            return;
        }
        next();
    });

    // we want to redirect users to the support page if they dont have support
    // page('*', async (context, next) => {
    //     if (context.path != '/dashboard/support/new') {
    //         if (!getCurrentSupport(context.store.getState())) {
    //             return page.redirect('/dashboard/support/new');
    //         }
    //     }
    //     next();
    // });
};

const setRouteMiddleware = () => {
    page('*', (context, next) => {
        context.store.dispatch(setRouteAction(context.pathname, context.query));
        next();
    });
};

const renderLayout = (reduxStore) => {
    const ReduxLayout = controller.ReduxWrappedLayout;

    const layoutElement = React.createElement(ReduxLayout, {
        store: reduxStore,
    });

    ReactDom.render(layoutElement, document.getElementById('root'));

    debug('Main layout rendered.');
};

export const configureReduxStore = (reduxStore, currentUser) => {
    debug('Executing Towersystems configure Redux store.');

    if (currentUser.get()) {
        reduxStore.dispatch(setCurrentUser(currentUser.get()));
    }
};

/**
 * [description]
 *
 * @param  {[type]} reduxStore [description]
 * @return {[type]}            [description]
 */
export const setupMiddlewares = (currentUser, reduxStore) => {
    debug('Executing setup middlewares.');

    setupContextMiddleware(reduxStore);
    
    loggedInMiddleware(currentUser, reduxStore);
    //loggedOutMiddleware(currentUser);
    loadSectionsMiddleware();
    setRouteMiddleware();
};

const boot = (currentUser) => {
    const reduxStore = createReduxStore({}, initialReducer);
    setReduxStore(reduxStore);
    configureReduxStore(reduxStore, currentUser);
    setupMiddlewares(currentUser, reduxStore);
    page.start({ decodeURLComponents: false });
};

export const bootApp = (appName) => {
    const user = userFactory();
    user.initialize().then(() => {
        debug(`Starting ${appName}. Let's do this.`);
        boot(user);
    });
};
